/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-organisation-product-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';

import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import { useTranslation, useBoolean } from 'hooks';
import {
    OrganisationProductFields,
    OrganisationProgramFields,
    OrganisationFieldsForProductProgram,
    OrganisationFieldsForProductProgram_Organisation_programs_OrganisationProgram as OrganisationFieldsForProductProgram_programs,
    OrganisationFieldsForProductProgram_Organisation_studies_Study as OrganisationFieldsForProductProgram_studies,
} from 'queries/generated/fragments.generated';
import { SAVE_ORGANISATION_PRODUCT, SAVE_ORGANISATION_PROGRAM } from 'queries/org-admin';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';
import { checkIfNameExist, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface IAddProduct {
    open: boolean;
    close(action: string): any;
    organisation: OrganisationFieldsForProductProgram;
    currentProduct?: OrganisationProductFields;
    currentProgram?: OrganisationProgramFields;
}

const AddProductDialog = ({ open, close, currentProduct = null, organisation, currentProgram }: IAddProduct) => {
    const { t } = useTranslation();
    const initialState = { name: '', referenceNumber: '', registrationNumber: '', activeIngredients: '' };
    const initialError = { name: '', registrationNumber: '', activeIngredients: '' };
    const [state, setState] = useState({ ...initialState });
    const [error, setError] = useState({ ...initialError } as any);
    const [saveProduct] = useMutation(SAVE_ORGANISATION_PRODUCT);
    const [saveProgram] = useMutation(SAVE_ORGANISATION_PROGRAM);

    const saving = useBoolean(false);
    const disabledSave = useBoolean(true);

    const countAssociatedStudies = (studies: OrganisationFieldsForProductProgram_studies[]) =>
        studies?.reduce((sum: number, item: OrganisationFieldsForProductProgram_studies) => {
            if (item.productsIds?.findIndex((productId: any) => productId === currentProduct?.id) >= 0) return sum + 1;
            return sum;
        }, 0);

    const countAssociatedPrograms = (programs: OrganisationFieldsForProductProgram_programs[]) =>
        programs?.reduce((sum: number, program: OrganisationFieldsForProductProgram_programs) => {
            if (program?.productIds?.findIndex((productId: any) => productId === currentProduct?.id) >= 0)
                return sum + 1;
            return sum;
        }, 0);

    useEffect(() => {
        if (currentProduct)
            setState({
                name: currentProduct?.name,
                referenceNumber: currentProduct?.referenceNumber,
                registrationNumber: currentProduct?.registrationNumber,
                activeIngredients: currentProduct?.activeIngredients,
            });
        else setState({ ...initialState });
    }, [currentProduct]);

    const onClose = (action?: string) => {
        if (action !== 'error') {
            disabledSave.setTrue();
            setState({ ...initialState });
            setError({ ...initialError });
        }
        close(action);
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const errorField = validateFields(value, field, length, required);
        setError({
            ...error,
            ...errorField,
        });
    };

    const onChange = (fieldName: string) => (event: any) => {
        disabledSave.setFalse();
        const { value } = event.target;
        let length = 50;
        if (fieldName === 'name' || fieldName === 'registrationNumber') length = 70;
        if (fieldName === 'activeIngredients') length = 500;

        setState({ ...state, [fieldName]: value });

        validate(value, fieldName, length, false);
    };

    const submit = () => {
        const errorField = validateFields(state.name, 'name', 70, true);

        const submitData = {
            activeIngredients: state.activeIngredients,
            id: currentProduct ? currentProduct.id : null,
            name: state.name.trim(),
            registrationNumber: state.registrationNumber,
            referenceNumber: state.referenceNumber,
        };

        const isNameExist = checkIfNameExist(submitData, organisation?.products ?? [], 'name') ?? false;

        if (
            (isNameExist && currentProduct === null) ||
            (currentProduct && currentProduct.name !== state.name && isNameExist)
        ) {
            setError({ ...error, name: t('admin:productAlreadyExist') });
            return;
        }

        setError({
            ...error,
            ...errorField,
        });
        if (errorField && errorField.name) return;

        saving.setTrue();
        if (currentProgram)
            saveProduct({
                variables: {
                    product: {
                        ...submitData,
                    },
                },
            })
                .then((res: any) => {
                    const ids = currentProgram.productIds.concat(res.data.saveOrganisationProduct.id);
                    saveProgram({
                        variables: {
                            program: {
                                id: currentProgram?.id ?? null,
                                name: currentProgram?.name,
                                productIds: ids,
                                indicationIds: currentProgram?.indicationIds ?? [],
                            },
                        },
                        refetchQueries: [
                            {
                                query: GET_ORGANISATION_PROGRAM_PRODUCT,
                            },
                        ],
                    })
                        .then(() => {
                            saving.setFalse();
                            onClose('add');
                        })
                        .catch(() => {
                            saving.setFalse();
                            onClose('error');
                        });
                })
                .catch(() => {
                    saving.setFalse();
                    onClose('error');
                });
        else
            saveProduct({
                variables: {
                    product: {
                        activeIngredients: state.activeIngredients,
                        id: currentProduct ? currentProduct.id : null,
                        name: state.name,
                        referenceNumber: state.referenceNumber,
                        registrationNumber: state.registrationNumber,
                    },
                },
                refetchQueries: [
                    {
                        query: GET_ORGANISATION_PROGRAM_PRODUCT,
                    },
                ],
            })
                .then(() => {
                    saving.setFalse();
                    onClose(currentProduct ? 'edit' : 'add');
                })
                .catch(() => {
                    saving.setFalse();
                    onClose('error');
                });
    };

    const associatedStudies = countAssociatedStudies(organisation?.studies);
    const associatedPrograms = countAssociatedPrograms(organisation?.programs);

    const getButtonLabel = () => {
        if (currentProgram && !currentProduct) return 'common:createAssign';
        return currentProduct ? 'update' : 'add';
    };

    return (
        <>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open}
                >
                    <DialogTitle id="add-product-modal-title">
                        {currentProduct
                            ? t('admin:editProduct', { id: currentProduct?.recordId ?? currentProduct?.id })
                            : t('admin:addProduct')}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="add-product-dialog-content">
                        {associatedPrograms || associatedStudies ? (
                            <div className="wrap-warning-message-absolute">
                                <WarningForDialog
                                    firstNumber={associatedStudies}
                                    secondNumber={associatedPrograms}
                                    type="product"
                                    noMb
                                />
                            </div>
                        ) : null}
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <TextField
                                    label={`${t('admin:product')}*`}
                                    id="add-product-dialog-name"
                                    fullWidth
                                    value={state.name}
                                    onChange={onChange('name')}
                                    placeholder={t('admin:enterProduct')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-product-dialog-name-help-text"
                                            length={state.name?.length}
                                            max={70}
                                            error={error.name}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    className="before-select mb"
                                    error={!!error.name}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    label={t('admin:referenceNumber')}
                                    id="add-product-dialog-referenceNumber"
                                    fullWidth
                                    value={state.referenceNumber}
                                    onChange={onChange('referenceNumber')}
                                    placeholder={t('admin:enterReferenceNumber')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-product-dialog-referenceNumber-help-text"
                                            length={state.referenceNumber?.length}
                                            max={50}
                                            error={error.referenceNumber}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    className="before-select mb"
                                    error={!!error.referenceNumber}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <TextField
                                    label={t('admin:registrationNumber')}
                                    id="add-product-dialog-registrationNumber"
                                    fullWidth
                                    value={state.registrationNumber}
                                    onChange={onChange('registrationNumber')}
                                    placeholder={t('admin:enterRegistrationNumber')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-product-dialog-registrationNumber-help-text"
                                            length={state.registrationNumber?.length}
                                            max={70}
                                            error={error.registrationNumber}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    className="before-select mb"
                                    error={!!error.registrationNumber}
                                />
                            </FormControl>
                        </div>
                        <TextField
                            id="add-product-dialog-activeIngredients"
                            label={t('admin:activeIngredients')}
                            multiline
                            rows={4}
                            fullWidth
                            value={state.activeIngredients}
                            onChange={onChange('activeIngredients')}
                            size="small"
                            placeholder={t('admin:enterActiveIngredients')}
                            helperText={
                                <CharactersRemaining
                                    id="add-product-dialog-activeIngredients-help-text"
                                    length={state.activeIngredients?.length}
                                    max={500}
                                    error={error.activeIngredients}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            className="mb"
                            error={!!error.activeIngredients}
                        />

                        <DialogActions
                            className={`add-product-dialog-content-actions ${!currentProduct ? 'flex-end' : ''}`}
                        >
                            <ModalUpdatedInfo item={currentProduct} />
                            <Button
                                className="add-product-btn"
                                disabled={
                                    disabledSave.value ||
                                    saving.value ||
                                    Object.keys(error).filter((item) => error[item]).length > 0
                                }
                                text={getButtonLabel()}
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default AddProductDialog;
